import Form from "@saleor/components/Form";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import {
  storeOrderListUrl,
  storeOrderUrl,
  StoreOrderUrlDialog,
  StoreOrderUrlQueryParams
} from "@saleor/storeOrders/urls";
import {
  LockStatus,
  OrderEventsEnum,
  SmopOrderStatusEnum,
  SmopUserTypeCode
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import {
  canManageAllMalls,
  canManageStoreOrders,
  canViewMasterFulfillment
} from "@saleor/utils/handlers/userHandler";
import React, { Fragment, useState } from "react";

import { useAuth } from "../../../auth/AuthProvider";
import { maybe } from "../../../misc";
import ReverseCancelDialog from "../../components/ReverseCancelDialog";
import StoreOrderAddProduct from "../../components/StoreOrderAddProduct";
import StoreOrderAssignRunnerDialog from "../../components/StoreOrderAssignRunnerDialog";
import StoreOrderCancelDialog from "../../components/StoreOrderCancelDialog";
import StoreOrderConfirmDialog from "../../components/StoreOrderConfirmationDialog";
import StoreOrderDetailsPage from "../../components/StoreOrderDetailsPage";
import StoreOrderEditDialog from "../../components/StoreOrderEditDialog";
import StoreOrderOverrideDialog from "../../components/StoreOrderOverrideDialog";
import StoreOrderPickDialog from "../../components/StoreOrderPickDialog";
import { pickItems } from "../../initialData";
import {
  TypedStoreOrderUpdateMutation,
  useAssignRunnerMutation,
  useOpsAdminScanParcel,
  useOrderLineAdd,
  useOrderLineRemove,
  useOrderLineUpdateQuantity,
  useOrderLineUpdateStatus,
  useOrderRevertFromCancelled,
  useRunnerScanParcel,
  useSetParcelNo
} from "../../mutation";
import {
  TypedOrderDetailsQuery,
  useProductList,
  useSearchRunner
} from "../../queries";
import {
  filterIsVisibilityProducts,
  handleUpdateStatusNotify,
  onSubmitAddProduct,
  onSubmitOpsScan,
  onSubmitOrderLineStatus,
  onSubmitOrderLineUpdateQty,
  onSubmitParcelNo,
  onSubmitRemoveProduct,
  onSubmitRunnerScan,
  storeOrderMutationComplete
} from "./../../handlers";

interface StoreOrderDetailsProps {
  id: string;
  params: StoreOrderUrlQueryParams;
}

export const StoreOrderDetails: React.FC<StoreOrderDetailsProps> = ({
  id,
  params
}) => {
  const {
    user: { userType }
  } = useAuth();
  const user = useUser();
  const isStoreAdmin =
    user.user?.userType.code === SmopUserTypeCode.STORE_ADMIN;
  const navigate = useNavigator();
  const notify = useNotifier();
  const handleBack = () => navigate(storeOrderListUrl());
  const disableAction = !canManageStoreOrders();
  const enableManageMallActions = canManageAllMalls();
  const enableViewMasterFulfillment = canViewMasterFulfillment();
  const [orderMallId, setOrderMallId] = React.useState("");

  const { search, loadMore, result } = useSearchRunner({
    skip: !orderMallId,
    variables: {
      first: 100,
      malls: [orderMallId],
      query: "",
      status: LockStatus.ACTIVE,
      userType: [SmopUserTypeCode.RUNNER]
    }
  });

  const {
    search: searchProduct,
    loadMore: loadMoreProduct,
    result: resultProduct
  } = useProductList({
    skip: !isStoreAdmin,
    variables: { first: 100, query: "" }
  });

  const filteredProducts = filterIsVisibilityProducts(
    maybe(() => resultProduct?.data.search.edges)
  );

  const [openModal, closeModal] = createDialogActionHandlers<
    StoreOrderUrlDialog,
    StoreOrderUrlQueryParams
  >(navigate, params => storeOrderUrl(id, params), params);

  const [hasPicking, setHasPicking] = useState(false);
  const [hasOutOfStock, setHasOutOfStock] = useState(false);

  return (
    <TypedOrderDetailsQuery displayLoader variables={{ id }}>
      {({ data, refetch, loading }) => {
        const storeOrder = data?.storeOrder;
        const storeStatus = data?.storeOrder.smopStatus;
        const isParcelIDValidated = data?.storeOrder.events.some(
          e => e.type === OrderEventsEnum.STORE_ORDER_SCANNED_BY_OMI_OPS
        );
        const deliveryType = maybe(
          () => data?.storeOrder?.order?.shippingMethod?.deliveryType
        );

        const { NEW_ORDER, ACKNOWLEDGED, READY } = SmopOrderStatusEnum;

        const storeOrderLines = data?.storeOrder && data?.storeOrder.lines;

        if (storeOrderLines) {
          const indexNum = storeOrderLines.length - 1;

          const allStoreStatus = [];

          for (let o = 0; o <= indexNum; o++) {
            allStoreStatus.push(storeOrderLines[o].status);
          }

          if (allStoreStatus.includes("OUT_OF_STOCK")) {
            setHasOutOfStock(true);
          } else {
            setHasOutOfStock(false);
          }

          if (allStoreStatus.includes("PICKING")) {
            setHasPicking(true);
          } else {
            setHasPicking(false);
          }
        }

        const [assignRunner] = useAssignRunnerMutation({
          onCompleted: data =>
            storeOrderMutationComplete(
              "ASSIGN_RUNNER",
              data,
              message => notify(message),
              navigate,
              storeOrderUrl,
              id,
              refetch
            ),
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [setParcelNo] = useSetParcelNo({
          onCompleted: data =>
            storeOrderMutationComplete(
              "SET_PARCEL",
              data,
              message => notify(message),
              navigate,
              storeOrderUrl,
              id,
              refetch
            ),
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [runnerScanParcel] = useRunnerScanParcel({
          onCompleted: data =>
            storeOrderMutationComplete(
              "RUNNER_SCAN_PARCEl",
              data,
              message => notify(message),
              navigate,
              storeOrderUrl,
              id,
              refetch
            ),
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [opsScanParcel] = useOpsAdminScanParcel({
          onCompleted: data =>
            storeOrderMutationComplete(
              "OPS_SCAN_PARCEL",
              data,
              message => notify(message),
              navigate,
              storeOrderUrl,
              id,
              refetch
            ),
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [removeProduct] = useOrderLineRemove({
          onCompleted: data =>
            storeOrderMutationComplete(
              "REMOVE_PRODUCT",
              data,
              message => notify(message),
              navigate,
              storeOrderUrl,
              id,
              refetch
            ),
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [addProduct] = useOrderLineAdd({
          onCompleted: data =>
            storeOrderMutationComplete(
              "ADD_PRODUCT",
              data,
              message => notify(message),
              navigate,
              storeOrderUrl,
              id,
              refetch
            ),
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [updateOrderLineStatus] = useOrderLineUpdateStatus({
          onCompleted: data =>
            storeOrderMutationComplete(
              "UPDATE_ORDER_LINE_STATUS",
              data,
              message => notify(message),
              navigate,
              storeOrderUrl,
              id,
              refetch
            ),
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [updateOrderLineQuantity] = useOrderLineUpdateQuantity({
          onCompleted: data =>
            storeOrderMutationComplete(
              "UPDATE_ORDER_QUANTITY",
              data,
              message => notify(message),
              navigate,
              storeOrderUrl,
              id,
              refetch
            ),
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        const [revertCancelledOrder] = useOrderRevertFromCancelled({
          onCompleted: data =>
            storeOrderMutationComplete(
              "REVERT_CANCELLED_STATUS",
              data,
              message => notify(message),
              navigate,
              storeOrderUrl,
              id,
              refetch
            ),
          onError: errors => {
            const [error] = errors.graphQLErrors;
            notify({
              status: "error",
              text: error?.message
            });
          }
        });

        return (
          <TypedStoreOrderUpdateMutation
            onCompleted={data => {
              handleUpdateStatusNotify(
                data,
                storeStatus,
                navigate,
                refetch,
                storeOrderUrl,
                id,
                message => notify(message)
              );
            }}
            onError={errors => {
              const [error] = errors.graphQLErrors;
              notify({
                status: "error",
                text: error?.message
              });
            }}
          >
            {statusUpdate => {
              const UpdateStatus = async variables => {
                const result = await statusUpdate({
                  variables: {
                    id,
                    ...variables
                  }
                });
                return result?.data?.updateStoreOrderStatus;
              };

              const handleUpdateStatus = data => {
                switch (data.type) {
                  case "UPDATE_STATUS":
                    UpdateStatus({ smopStatus: data.status });
                    break;
                  case "CANCEL_ORDER":
                    UpdateStatus({
                      cancelReason: data.cancelReason,
                      smopStatus: data.status
                    });
                    break;
                  case "OVERRIDE_STATUS":
                    UpdateStatus({
                      overrideReason: data.overrideReason,
                      smopStatus: data.status
                    });
                    break;
                  default:
                    break;
                }
              };

              return (
                <Fragment>
                  <StoreOrderDetailsPage
                    storeOrder={storeOrder}
                    disabled={loading}
                    onBack={handleBack}
                    onModal={action => openModal(action)}
                    onRowClick={getID => () =>
                      openModal("onClickStoreOrderProduct", {
                        productId: getID
                      })}
                    onNextPage={() => undefined}
                    onPreviousPage={() => undefined}
                    ConfirmDialog={getStatus =>
                      openModal("onClickSaveButton", {
                        orderStatus: getStatus
                      })
                    }
                    orderStatus={data?.storeOrder?.smopStatus}
                    addProductDialog={getID =>
                      openModal("onClickAddProduct", { productId: getID })
                    }
                    userType={maybe(() => userType.code, "")}
                    assignRunnerDialog={() =>
                      openModal("onClickAssignRunner", {
                        productId: storeOrder.id
                      })
                    }
                    editProductDialog={productIds =>
                      openModal("onClickEditProduct", {
                        productIds
                      })
                    }
                    onSubmitParcelNo={parcelNo =>
                      onSubmitParcelNo(parcelNo, setParcelNo, id)
                    }
                    onSubmitRunnerScan={parcelNo =>
                      onSubmitRunnerScan(parcelNo, runnerScanParcel, id)
                    }
                    onSubmitOpsScan={parcelNo =>
                      onSubmitOpsScan(parcelNo, opsScanParcel, id)
                    }
                    onSubmitOrderLineStatus={(lineIds, status) =>
                      onSubmitOrderLineStatus(
                        lineIds,
                        status,
                        updateOrderLineStatus,
                        id
                      )
                    }
                    overrideStatusDialog={() => openModal("onOverrideStatus")}
                    reverseCancel={() => openModal("onReverseCancel")}
                    disableAction={disableAction}
                    enableManageMallActions={enableManageMallActions}
                    enableViewMasterFulfillment={enableViewMasterFulfillment}
                  />

                  <Form
                    onSubmit={handleUpdateStatus}
                    initial={{
                      cancelInput: "",
                      cancelReason: "",
                      cancelType: ""
                    }}
                  >
                    {({ change, data }) => (
                      <StoreOrderCancelDialog
                        open={params.action === "cancel"}
                        onClose={closeModal}
                        handleSubmit={handleUpdateStatus}
                        onChange={change}
                        data={data}
                        name="cancelType"
                      />
                    )}
                  </Form>

                  <StoreOrderPickDialog
                    open={params.action === "onClickStoreOrderProduct"}
                    onClose={closeModal}
                    orderId={maybe(() => params.productId, "")}
                    orderStatus={maybe(() => storeOrder.smopStatus)}
                    variant={maybe(
                      () =>
                        storeOrder.lines.find(
                          ({ id }) => params.productId === id
                        ),
                      pickItems
                    )}
                    onSubmitOrderLineStatus={(lineIds, status) =>
                      onSubmitOrderLineStatus(
                        lineIds,
                        status,
                        updateOrderLineStatus,
                        id
                      )
                    }
                    editProductDialog={productIds =>
                      openModal("onClickEditProduct", {
                        productIds
                      })
                    }
                  ></StoreOrderPickDialog>

                  <StoreOrderConfirmDialog
                    open={params.action === "onClickSaveButton"}
                    onClose={closeModal}
                    orderStatus={maybe(
                      () => params.orderStatus,
                      SmopOrderStatusEnum.NEW_ORDER
                    )}
                    handleSubmit={handleUpdateStatus}
                    userType={maybe(() => userType.code, "")}
                    isParcelIDValidated={isParcelIDValidated}
                    deliveryType={deliveryType}
                    hasPicking={hasPicking}
                    hasOutOfStock={hasOutOfStock}
                    storeOrderLines={storeOrderLines}
                  ></StoreOrderConfirmDialog>

                  <StoreOrderEditDialog
                    open={params.action === "onClickEditProduct"}
                    productIds={maybe(() => params.productIds, {})}
                    onClose={closeModal}
                    onSubmitRemoveProduct={lineIds =>
                      onSubmitRemoveProduct(lineIds, removeProduct, id)
                    }
                    onSubmitOrderLineStatus={(lineIds, status) =>
                      onSubmitOrderLineStatus(
                        lineIds,
                        status,
                        updateOrderLineStatus,
                        id
                      )
                    }
                    onSubmitOrderLineUpdateQty={lines =>
                      onSubmitOrderLineUpdateQty(
                        lines,
                        updateOrderLineQuantity,
                        id
                      )
                    }
                  ></StoreOrderEditDialog>

                  <StoreOrderAssignRunnerDialog
                    open={params.action === "onClickAssignRunner"}
                    runnerList={maybe(() =>
                      result.data.search.edges
                        .map(edge => edge.node)
                        .filter(suggestedProduct => suggestedProduct.id)
                    )}
                    runner={storeOrder?.runner}
                    onClose={closeModal}
                    fetchRunner={search}
                    fetchMoreRunner={{
                      hasMore: maybe(
                        () => result.data.search.pageInfo.hasNextPage
                      ),
                      loading: result.loading,
                      onFetchMore: loadMore
                    }}
                    loading={result.loading}
                    onSubmit={runnerId =>
                      assignRunner({
                        variables: {
                          runnerId,
                          storeOrderId: storeOrder.id
                        }
                      })
                    }
                    orderMallId={storeOrder?.mall?.id}
                    setOrderMallId={setOrderMallId}
                  ></StoreOrderAssignRunnerDialog>

                  <StoreOrderAddProduct
                    hasMore={resultProduct.data?.search?.pageInfo.hasNextPage}
                    open={params.action === "onClickAddProduct"}
                    onClose={closeModal}
                    products={filteredProducts}
                    onSubmitAddProduct={product =>
                      onSubmitAddProduct(
                        product,
                        addProduct,
                        id,
                        storeOrder.tenant.id
                      )
                    }
                    onFetch={searchProduct}
                    onFetchMore={loadMoreProduct}
                    loading={resultProduct.loading}
                  ></StoreOrderAddProduct>

                  <StoreOrderOverrideDialog
                    onClose={closeModal}
                    open={params.action === "onOverrideStatus"}
                    orderStatus={storeStatus}
                    newOrderStatus={
                      storeStatus === NEW_ORDER
                        ? ACKNOWLEDGED
                        : storeStatus === ACKNOWLEDGED
                        ? READY
                        : ""
                    }
                    onSubmitOverride={overrideReason =>
                      handleUpdateStatus({
                        overrideReason,
                        status:
                          storeStatus === NEW_ORDER
                            ? ACKNOWLEDGED
                            : storeStatus === ACKNOWLEDGED
                            ? READY
                            : "",
                        type: "OVERRIDE_STATUS"
                      })
                    }
                  />

                  <ReverseCancelDialog
                    onClose={closeModal}
                    open={params.action === "onReverseCancel"}
                    onSubmit={(revertReason: string) =>
                      revertCancelledOrder({
                        variables: {
                          id,
                          revertReason
                        }
                      })
                    }
                  />
                </Fragment>
              );
            }}
          </TypedStoreOrderUpdateMutation>
        );
      }}
    </TypedOrderDetailsQuery>
  );
};

export default StoreOrderDetails;
